import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import translations from './../../utils/translations';

import { theme } from '../../theme';
import { styled } from '@material-ui/core/styles';

import Card from '@material-ui/core/Card';
import Grid from '@material-ui/core/Grid';

import DeleteIcon from '@material-ui/icons/Delete';
import NotesIcon from '@material-ui/icons/Notes';
import StarOutlineIcon from '@material-ui/icons/StarOutline';
import StarIcon from '@material-ui/icons/Star';

import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import PersonAddIcon from '@material-ui/icons/PersonAdd';

import {
  SdmTypography,
  SdmTextButton,
  SdmIconButtonLoader,
  SdmDialog,
} from '..';
import { breakoutRoomInviteModel } from '../../entities/breakoutRoomInviteModel';
import { CustomFormTextField } from 'pages/breakout/brandArea/BreakoutRoomForm';

const StyledCard = styled(Card)(({ xsdown }) => ({
  backgroundColor: theme.colors.primary.white,
  borderLeft: `${xsdown ? 5 : 10}px solid ${theme.colors.primary.red}`,
  width: `calc(100% - 10px)`,
  padding: xsdown ? '4px 17px 4px 12px' : '4px 28px 4px 7px',
  borderRadius: 0,
}));

const CustomCard = (
  {
    cardInformations,
    onDelete,
    onBan,
    onPromote,
    onRoleEdit,
    onShowNote,
    isXsDown,
    is320Down,
    isPromotionEnabled,
    ...rest
  },
  ref
) => {
  const { cardTitle, status, jobTitle } = cardInformations;
  // Hooks
  const intl = useIntl();
  const i18n = translations.personalArea(intl).cards;

  const [promotion, setPromotion] = useState();
  const [dialog, setDialog] = useState({
    isOpen: false,
    speakerRole: jobTitle,
  });

  useEffect(() => {
    if (cardInformations) {
      setPromotion(getUserInvitationTypes());
    }
  }, [cardInformations]);

  const handleBan = () => {
    onBan && onBan();
  };

  const handleDelete = () => {
    onDelete && onDelete();
  };

  const handleShowNote = () => {
    onShowNote && onShowNote();
  };

  const handlePromote = async () => {
    if (isPromotionEnabled) {
      const promotionType = breakoutRoomInviteModel.enums.invitationTypes.VIP;
      const inputPromotion = promotion === promotionType ? null : promotionType;
      setPromotion(inputPromotion);
      if (onPromote) return await onPromote(inputPromotion);
    }
  };

  const getPromotionTypes = () => {
    const types = breakoutRoomInviteModel.enums.invitationTypes;
    const style = {
      color: theme.colors.primary.red,
    };
    return [
      {
        type: types.VIP,
        icon: <StarOutlineIcon style={style} />,
        filledIcon: <StarIcon style={style} />,
      },
    ];
  };

  const getUserInvitationTypes = () => {
    const type = cardInformations?.invitationType;
    return type;
  };

  const promotionTypes = getPromotionTypes();

  const handleRoleCreation = async () => {
    onRoleEdit &&
      onRoleEdit({ id: cardInformations.id, jobTitle: dialog.speakerRole });
  };

  const handleRole = () => {
    setDialog((p) => ({ ...p, isOpen: true }));
  };

  const renderSpeakerRoleDialog = () => {
    if (!dialog.isOpen) return null;
    return (
      <SdmDialog
        open
        onClose={() => setDialog((p) => ({ ...p, isOpen: false }))}
        agreeText={i18n.speakerDialog.btn}
        onAgree={handleRoleCreation}
        paperStyle={{
          borderRadius: 15,
          width: 410,
          height: 260,
          padding: 0,
          margin: 0,
        }}
        closeIconContainerStyle={{
          top: 15,
          right: 20,
          transform: 'scale(1.5)',
        }}
        actionContainerStyle={{ justifyContent: 'center' }}
        actionStyle={{ height: 47, width: 192, margin: 0 }}
        actionTextStyle={{ fontSize: 20 }}
        titleStyle={{ padding: 0 }}
        contentStyle={{
          paddingTop: 16,
          flex: 'none',
          margin: '0 auto',
        }}
        showTitleDivider={false}
      >
        <div style={{ padding: '10px 20px' }}>
          <SdmTypography variant={'h1'} textAlign={'center'}>
            {i18n.speakerDialog.text}
          </SdmTypography>
          <CustomFormTextField
            style={{ width: '100%' }}
            value={dialog.speakerRole}
            onChange={(e) =>
              setDialog((p) => ({ ...p, speakerRole: e.target.value }))
            }
          />
        </div>
      </SdmDialog>
    );
  };

  return (
    <StyledCard ref={ref} {...rest} xsdown={isXsDown ? 1 : 0} elevation={0}>
      <Grid container alignItems={'center'}>
        {!!promotion && renderSpeakerRoleDialog()}
        {promotionTypes.length > 0 && onPromote ? (
          <Grid item md={1} sm={onBan ? 1 : 1} xs={2}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <SdmIconButtonLoader
                callback={isPromotionEnabled && handlePromote}
                cursorType={isPromotionEnabled ? 'pointer' : 'default'}
              >
                {promotionTypes[0].type === promotion
                  ? promotionTypes[0].filledIcon
                  : promotionTypes[0].icon}
              </SdmIconButtonLoader>
              {!!promotion && (
                <Tooltip title={i18n.speakerRole}>
                  <PersonAddIcon
                    style={{
                      color: theme.colors.primary.red,
                      cursor: 'pointer',
                      paddingBottom: 4,
                    }}
                    onClick={isPromotionEnabled && handleRole}
                  />
                </Tooltip>
              )}
            </div>
          </Grid>
        ) : null}
        <Grid item md={onPromote ? 5 : 6} sm={onBan ? 4 : 5} xs={10}>
          <Grid
            container
            alignItems={'center'}
            justify={isXsDown ? 'center' : 'flex-start'}
          >
            <SdmTypography variant={'h4'}>{cardTitle}</SdmTypography>
          </Grid>
        </Grid>
        <Grid item md={2} sm={onBan ? 2 : 3} xs={onBan ? 4 : 6}>
          <Grid
            container
            alignItems={'center'}
            justify={'space-around'}
            style={{ minHeight: 43 }}
          >
            <SdmTypography
              variant={'text'}
              margin={0}
              style={{
                color: theme.colors.primary.grey,
                textTransform: 'uppercase',
              }}
            >
              {status}
            </SdmTypography>
          </Grid>
        </Grid>

        <Grid item md={4} sm={onBan ? 5 : 3} xs={onBan ? 8 : 6}>
          <Grid
            container
            justify={isXsDown ? 'space-around' : 'flex-end'}
            alignItems={'center'}
          >
            {onBan ? (
              <SdmTextButton
                variant={'red'}
                text={i18n.remove}
                textVariant={'h4Regular'}
                onClick={handleBan}
                style={{
                  minWidth: isXsDown ? 30 : 90,
                  maxWidth: is320Down ? 95 : '100%',
                  height: isXsDown ? 26 : 36,
                }}
              />
            ) : null}
            {onShowNote ? (
              <Tooltip title={i18n.showNotes}>
                <IconButton aria-label="notes" onClick={handleShowNote}>
                  <NotesIcon
                    style={{
                      color: theme.colors.primary.red,
                    }}
                  />
                </IconButton>
              </Tooltip>
            ) : null}
            {onDelete ? (
              <Tooltip title={i18n.delete}>
                <IconButton
                  aria-label="delete"
                  style={{ padding: isXsDown ? 5 : 12 }}
                  onClick={handleDelete}
                >
                  <DeleteIcon
                    style={{
                      color: theme.colors.primary.red,
                    }}
                  />
                </IconButton>
              </Tooltip>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    </StyledCard>
  );
};

CustomCard.displayName = 'SmallerCard';

export const SmallerCard = React.forwardRef(CustomCard);
